import { DateRangePicker, Tooltip, getDate, setDateWithTimezone } from '@systemeio/ui'
import ContactPaymentsPaymentTooltip from 'modules/contacts/contact-page/components/contact-paymnet-tooltip'
import { ContactSubscriptionsStatusEnum } from 'modules/contacts/contact-page/enums/contact-subscriptions-enum'
import { useContactSubscriptions } from 'modules/contacts/contact-page/hooks/use-contact-subscriptions'
import { ContactPageFilterInterface } from 'modules/contacts/contact-page/types/contact-page-filter-interface'
import { ContactSubscriptionsInterface } from 'modules/contacts/contact-page/types/contact-subscriptions-interface'
import { getContactPaymentUrl } from 'modules/contacts/contact-page/utils/get-contact-payment-url'
import { ContactId, ContactInterface } from 'modules/contacts/types/contact-interface'
import UnsubscribeModal from 'modules/payment/subscriptions/components/unsubscribe-modal'
import React, { useEffect } from 'react'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import { OrderEnum } from 'shared/types/pagination-interface'
import { dateFormat, dateTimeFormat } from 'shared/utils/date-time-format'
import { getDateRangeSinceDate, getToday } from 'shared/utils/get-dates'
import { BlockedIcon, CheckIcon, PercentIcon } from '@systemeio/ui'

interface ContactSubscriptionsProps {
  contactId: ContactId
  contact?: ContactInterface
}

const defaultFilters: ContactPageFilterInterface = { date: [null, null] }

const ContactSubscriptions = ({ contactId, contact }: ContactSubscriptionsProps) => {
  const { t } = useLocoTranslation()
  const { user, intlLocale } = useUser()
  const { setPagination, pagination, setLimit, limit, filter, setFilter } = useFilterPagination(
    defaultFilters,
    true,
  )
  const { contactSubscriptions, mutate } = useContactSubscriptions({
    contactId,
    limit,
    pagination,
    filter,
    shouldFetch: filter.date[0] !== null,
  })

  const onUnsubscribe = async (subscription: ContactSubscriptionsInterface) => {
    await mutate(
      data =>
        data && {
          items: data.items.map(item => {
            if (item.id === subscription.id)
              item = {
                ...item,
                subscriptionItems: item.subscriptionItems.map(subscriptionItem => ({
                  ...subscriptionItem,
                  status: ContactSubscriptionsStatusEnum.cancelled,
                  ...(subscriptionItem.status === ContactSubscriptionsStatusEnum.active
                    ? {
                        deactivatedAt: setDateWithTimezone(undefined, user?.timezone).toISOString(),
                      }
                    : {}),
                })),
              }
            return item
          }),
          hasMore: data.hasMore,
        },

      false,
    )
  }

  useEffect(() => {
    if (contact) {
      setFilter({ date: getDateRangeSinceDate(contact.createdAt, user?.timezone) })
    }
  }, [contact, setFilter, user?.timezone])

  const columns: ColumnDefinitionInterface<ContactSubscriptionsInterface>[] = [
    {
      key: 'subscriptionItems',
      header: t('dashboard.contact_page.plan'),
      onRender: subscription => (
        <div className={'flex w-full flex-col truncate'}>
          {subscription.subscriptionItems.map((el, idx) => (
            <div
              key={idx}
              className={`py-3 ${idx !== 0 && 'border-text-secondary/30 border-t'} ${
                idx === 0 && 'pt-0'
              } ${idx + 1 === subscription.subscriptionItems.length && 'pb-0'}`}
            >
              <div className={'flex items-center justify-between gap-1.5'}>
                <div className={'flex items-center gap-1.5 truncate'}>
                  <ContactPaymentsPaymentTooltip orderItem={el.orderItem} />
                  {subscription.customer.referredBy && (
                    <Tooltip label={subscription.customer.referredBy.email ?? ''} mode={'hover'}>
                      <PercentIcon className={'fill-text-tertiary'} />
                    </Tooltip>
                  )}
                </div>
                {el.status === ContactSubscriptionsStatusEnum.active ? (
                  <Tooltip mode={'hover'} label={t('global.active')}>
                    <CheckIcon className={'stroke-green'} />
                  </Tooltip>
                ) : (
                  <Tooltip
                    mode={'hover'}
                    label={`${t('global.cancelled')} ${
                      el.deactivatedAt
                        ? '(' +
                          dateTimeFormat(el.deactivatedAt, {
                            timeFormat: user?.timeFormat,
                            dateFormat: user?.dateFormat,
                            timeZone: user?.timezone,
                          }) +
                          ')'
                        : ''
                    }`}
                  >
                    <BlockedIcon className={'fill-danger'} />
                  </Tooltip>
                )}
              </div>
            </div>
          ))}
        </div>
      ),
      onRenderSmall: subscription => (
        <div className={'flex flex-col truncate'}>
          {subscription.subscriptionItems.map((el, idx) => (
            <div
              key={idx}
              className={`truncate py-3 ${idx !== 0 && 'border-text-secondary/30 border-t'} ${
                idx === 0 && 'pt-0'
              } ${idx + 1 === subscription.subscriptionItems.length && 'pb-0'}`}
            >
              <div className={'flex items-center gap-1.5'}>
                {el.orderItem.offer &&
                el.orderItem.offer.removedAt === null &&
                el.orderItem.offer.funnelStep ? (
                  <LinkWithoutPrefetch
                    rel="noreferrer"
                    className="primary-link w-full truncate"
                    href={getContactPaymentUrl(
                      el.orderItem.offer.funnelStep.funnel.id,
                      el.orderItem.offer.funnelStep.id,
                    )}
                  >
                    {el.orderItem.pricePlan
                      ? el.orderItem.pricePlan.innerName
                      : el.orderItem.productOrderItem && el.orderItem.productOrderItem.product.name}
                  </LinkWithoutPrefetch>
                ) : (
                  <span>{el.orderItem.pricePlan.innerName}</span>
                )}
                {subscription.customer.referredBy && (
                  <PercentIcon className={'fill-text-tertiary'} />
                )}
                {el.status === ContactSubscriptionsStatusEnum.active ? (
                  <Tooltip mode={'hover'} label={t('global.active')}>
                    <CheckIcon className={'stroke-green'} />
                  </Tooltip>
                ) : (
                  <Tooltip
                    mode={'hover'}
                    label={`${t('global.cancelled')} ${
                      el.deactivatedAt
                        ? '(' +
                          dateTimeFormat(el.deactivatedAt, {
                            timeFormat: user?.timeFormat,
                            dateFormat: user?.dateFormat,
                            timeZone: user?.timezone,
                          }) +
                          ')'
                        : ''
                    }`}
                  >
                    <BlockedIcon className={'fill-danger'} />
                  </Tooltip>
                )}
              </div>
            </div>
          ))}
        </div>
      ),
      widthPercent: 80,
    },
    {
      key: 'createdAt',
      widthPercent: 20,
      header: t('global.created_on'),
      onRender: subscription => (
        <Tooltip
          wrapperClassName={'w-full'}
          mode={'hover'}
          label={dateTimeFormat(subscription.createdAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        >
          <span className={'w-full truncate'}>
            {dateFormat(subscription.createdAt, {
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            })}
          </span>
        </Tooltip>
      ),
    },
  ]
  return (
    <ListingLayout
      counter={false}
      data={contactSubscriptions}
      type={'default'}
      title={<span className={'text-[17px]'}>{t('dashboard.contact_page.subscriptions')}</span>}
      customHeaderElement={
        <DateRangePicker
          maxDate={getToday(user?.timezone)}
          minDate={getDate(contact?.createdAt, user?.timezone)}
          getDefaultDate={() => getDateRangeSinceDate(contact?.createdAt, user?.timezone)}
          date={filter.date}
          onChange={date => {
            setPagination({ startFromId: undefined, order: OrderEnum.next })
            setLimit(10)
            setFilter({ date: date })
          }}
          timezone={user?.timezone}
          createdAt={user?.createdAt}
          intlLocale={intlLocale}
        />
      }
    >
      <Table
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
        actions={{
          openLink: [
            {
              name: 'affiliate',
              caption: t('dashboard.user.affiliate'),
              shouldRender: subscription => subscription.customer.referredBy !== null,
              setHref: subscription => `/affiliate-program/${subscription.customer.referredBy?.id}`,
              isNext: true,
              openInNew: false,
            },
            {
              name: 'cc-info',
              caption: t('dashboard.contact_page.change_cc_info'),
              shouldRender: subscription => subscription.changeCCLink !== null,
              setHref: subscription =>
                `${process.env.NEXT_PUBLIC_SIO_HOST_SCHEME}:${subscription.changeCCLink}`,
              openInNew: true,
            },
          ],
          custom: [
            {
              name: 'unsubscribe',
              caption: t('dashboard.subscriptions.unsubscribe'),
              onActionRender: (opened, onClose, subscription, key) => (
                <UnsubscribeModal
                  key={key}
                  opened={opened}
                  onClose={onClose}
                  subscription={subscription}
                  onUnsubscribe={onUnsubscribe}
                />
              ),
              shouldRender: subscription =>
                subscription.subscriptionItems.some(
                  items => items.status === ContactSubscriptionsStatusEnum.active,
                ),
            },
          ],
        }}
        data={contactSubscriptions}
        columns={columns}
      />
    </ListingLayout>
  )
}

export default ContactSubscriptions
